import { Note } from './models/Note';

type Sound = { start: () => void; stop: () => void };

export default class Audio {
  constructor() {
    this.audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
  }

  private audioContext: AudioContext;
  private sound: Sound | null = null;

  public play(selectedNotes: Note[]): void {
    this.sound?.stop();
    this.sound = this.chord(selectedNotes);
    this.sound?.start();
  }

  private chord(selectedNotes: Note[]): Sound | null {
    if (selectedNotes.length === 0) {
      return null;
    }

    let started = false;
    const gainValue = 1 / selectedNotes.length;
    const gainDecay = 0.01;
    const gainNode = new GainNode(this.audioContext, {
      gain: 0,
    });

    const notes = selectedNotes.map(selectedNote => {
      const osc = this.note(selectedNote);
      osc.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      return osc;
    });

    return {
      start: () => {
        if (!started) {
          started = true;
          notes.forEach(note => {
            note.start();
          });
          gainNode.gain.setTargetAtTime(
            gainValue,
            this.audioContext.currentTime,
            gainDecay
          );
        }
      },
      stop: () => {
        if (started) {
          gainNode.gain.setTargetAtTime(
            0,
            this.audioContext.currentTime,
            gainDecay
          );
          notes.forEach(note => note.stop(this.audioContext.currentTime + 5));
        }
      },
    };
  }

  private note(note: Note): OscillatorNode {
    return new OscillatorNode(this.audioContext, {
      frequency: frequency(note, 4),
      periodicWave: this.waveform(),
    });
  }

  private waveform() {
    const overtones: number[] = [1, 0.1, 0.35, 0.05, 0.05, 0.05];
    const imag = new Float32Array([0, ...overtones]);
    const real = new Float32Array(imag.length);
    return this.audioContext.createPeriodicWave(real, imag);
  }
}

function frequency(note: Note, octave: number): number {
  const midiNumber = 12 * (octave + 1) + note;
  return 440 * Math.pow(2, (midiNumber - 69) / 12);
}
